import type { IconName } from '../components/ui/Icon'
import type { SimpleTKey } from '../lib/i18n'

export interface NavItem {
  to: string
  /**
   * The catalogue key, not the word. The rail's tooltip, the mobile bar's caption and the
   * command palette's "go to" row all resolve it with `useT()` at render, so the whole
   * navigation changes language in the same paint as everything else.
   */
  labelKey: SimpleTKey
  icon: IconName
  /** Only rendered for `user.isSuperadmin`. */
  superadmin?: boolean
  /** Shown in the mobile bottom bar; the rest fall into the overflow sheet. */
  primary?: boolean
  /**
   * Shown in Simple mode. Advanced mode shows every item regardless of this flag.
   *
   * The line is drawn at "something a brand touches most days" — publishing, replying, checking
   * the numbers, connecting an account, adjusting settings — versus the screens built for someone
   * who has already decided they need them (a saved report to hand a client, ad campaigns, a
   * competitor to watch). Nothing is hidden for good: `shell.nav.advanced` is one click away, and
   * every route stays reachable by URL in either mode — this is a display filter, not a permission.
   */
  simple?: boolean
  /**
   * A key into `NAV_GROUPS` — folds this item under a collapsible "Post"/"Andamento"/"Pubblicità"
   * row on the **desktop vertical rail only** (`NavRail`'s own rendering, via `toRailRows`). The
   * mobile bottom bar and `CommandPalette` both read `NAV_ITEMS` flat and ignore this field on
   * purpose: a 64 px icon rail benefits from folding related screens together, a 56 px bottom bar
   * with five slots and a Cmd+K search list do not.
   */
  groupKey?: string
  /** Draws a hairline above this item/group on the **desktop vertical rail only** — the same
   *  "rail-specific, flat list elsewhere" scope `groupKey` already has. Attached to whichever
   *  item renders the row (the first member, for a group); an item hidden by `simple` carries
   *  its divider out with it rather than leaving an orphaned line. */
  dividerBefore?: boolean
}

export interface NavGroupDef {
  labelKey: SimpleTKey
  icon: IconName
}

/** The three desktop-rail dropdown groups. Keyed by `NavItem.groupKey`. */
export const NAV_GROUPS: Readonly<Record<string, NavGroupDef>> = {
  posts: { labelKey: 'common.nav.group.posts', icon: 'planner' },
  trend: { labelKey: 'common.nav.group.trend', icon: 'analytics' },
  ads: { labelKey: 'common.nav.group.ads', icon: 'megaphone' },
}

/**
 * The product's whole navigation, in one list. The rail, the mobile bar and the command
 * palette all read it, so a new screen appears in all three by adding one row here.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  // First on purpose: a guided tour is often the very first thing a new account reaches for,
  // not the last — `NavRail` used to append its Simple/Advanced toggle right after whichever
  // item rendered last and parked Tours there; the toggle now just follows wherever the list
  // ends instead.
  { to: '/app/tours', labelKey: 'common.nav.tours', icon: 'info', simple: true },

  { to: '/app/dashboard', labelKey: 'common.nav.dashboard', icon: 'dashboard', primary: true, simple: true, dividerBefore: true },
  // `Brand` lives under the account menu (`UserMenu.tsx`), next to "Account settings" — switching
  // between brands is an account-level act, not a screen of its own in the day-to-day rail.
  { to: '/app/connections', labelKey: 'common.nav.connections', icon: 'link', simple: true },
  { to: '/app/inbox', labelKey: 'common.nav.inbox', icon: 'inbox', primary: true, simple: true },

  // "Post" — the planner and what shapes what goes in it: who else is posting, a rule that
  // publishes or replies unattended, and the link-in-bio page a post's caption points at.
  { to: '/app/planner', labelKey: 'common.nav.planner', icon: 'planner', primary: true, simple: true, groupKey: 'posts', dividerBefore: true },
  { to: '/app/competitors', labelKey: 'common.nav.sources', icon: 'users', groupKey: 'posts' },
  { to: '/app/automations', labelKey: 'common.nav.automations', icon: 'sparkle', groupKey: 'posts' },
  { to: '/app/smartlinks', labelKey: 'common.nav.smartlinks', icon: 'globe', groupKey: 'posts' },
  // "Pubblicità" — the two screens that spend budget.
  { to: '/app/ads', labelKey: 'common.nav.ads', icon: 'megaphone', groupKey: 'ads' },
  // `star`, not `megaphone` — the megaphone is the Ads screen's, and two rail entries wearing one
  // icon is how people click the wrong one.
  { to: '/app/campaigns', labelKey: 'common.nav.campaigns', icon: 'star', groupKey: 'ads' },
  // "Andamento" — the three ways to read how a brand is doing: a live board, a saved shareable
  // window, a frozen document to hand someone with no account.
  { to: '/app/analytics', labelKey: 'common.nav.analytics', icon: 'analytics', primary: true, simple: true, groupKey: 'trend' },
  { to: '/app/performance', labelKey: 'common.nav.performance', icon: 'analytics', groupKey: 'trend' },
  { to: '/app/reports', labelKey: 'common.nav.reports', icon: 'copy', groupKey: 'trend' },

  { to: '/app/studio', labelKey: 'common.nav.studio', icon: 'palette', dividerBefore: true },
  // Settings and billing live in the account menu (`UserMenu.tsx`, "Impostazioni dell'account" /
  // "Piano e fatturazione") — both routes stay reachable by URL, just not duplicated on the rail.
  { to: '/app/admin', labelKey: 'common.shell.adminConsole', icon: 'admin', superadmin: true },
]

export interface RailItemRow {
  kind: 'item'
  item: NavItem
}

export interface RailGroupRow {
  kind: 'group'
  key: string
  group: NavGroupDef
  items: NavItem[]
}

export interface RailDividerRow {
  kind: 'divider'
}

export type RailRow = RailItemRow | RailGroupRow | RailDividerRow

/** `NavRail`'s own view of `NAV_ITEMS`: consecutive-or-not, every item sharing a `groupKey` folds
 *  into one row at the position of its first occurrence — the desktop rail's dropdown groups.
 *  `dividerBefore` adds a hairline row ahead of whichever row that item ends up being part of.
 *  Everything else (`visibleNavItems`, the mobile bar, `CommandPalette`) keeps reading the flat
 *  list untouched. */
export const toRailRows = (items: readonly NavItem[]): RailRow[] => {
  const rows: RailRow[] = []
  const seen = new Set<string>()
  for (const item of items) {
    if (!item.groupKey) {
      if (item.dividerBefore) rows.push({ kind: 'divider' })
      rows.push({ kind: 'item', item })
      continue
    }
    if (seen.has(item.groupKey)) continue
    seen.add(item.groupKey)
    const group = NAV_GROUPS[item.groupKey]
    if (!group) {
      if (item.dividerBefore) rows.push({ kind: 'divider' })
      rows.push({ kind: 'item', item })
      continue
    }
    if (item.dividerBefore) rows.push({ kind: 'divider' })
    rows.push({
      kind: 'group',
      key: item.groupKey,
      group,
      items: items.filter((entry) => entry.groupKey === item.groupKey),
    })
  }
  return rows
}

/** localStorage value for `NavRail`'s Simple/Advanced switch. Simple is the default for everyone. */
export type NavMode = 'simple' | 'advanced'

export const visibleNavItems = (isSuperadmin: boolean): NavItem[] =>
  NAV_ITEMS.filter((item) => !item.superadmin || isSuperadmin)
